import * as Api from '@ViewModels';
import { QueryClient, QueryKey } from '@tanstack/react-query';
import { ServerSentEventMessage } from 'fetch-event-stream';
import { Draft, produce } from 'immer';

interface AIChatMessageError extends Api.AIChatMessage {
	error: string;
	chatRequest: Api.AIChatRequest;
}
// Client-only extension: backend marks Progress events as transport-only and never
// returns this field on a history GET. Set during streaming, cleared once real content
// arrives, and wiped wholesale by the success-invalidation refetch.
export interface AIChatMessageWithProgress extends Api.AIChatMessage {
	progress?: { stage: string; message: string };
}
interface PartialToolCall extends Api.AIToolCall {
	partial: Record<string, string>;
}

function isAIChatStreamTextResponse(sseData: Api.AIChatStreamResponse): sseData is Api.AIChatStreamMessageResponse {
	return Boolean((sseData as Api.AIChatStreamMessageResponse)?.message);
}

function isAIChatStreamToolCallResponse(sseData: Api.AIChatStreamResponse): sseData is Api.AIChatStreamToolResponse {
	return Boolean((sseData as Api.AIChatStreamToolResponse)?.toolCall);
}

function isAIChatStreamProgressResponse(sseData: unknown): sseData is Api.AIChatStreamProgressResponse {
	if (sseData == null || typeof sseData !== 'object') return false;
	const candidate = sseData as Partial<Api.AIChatStreamProgressResponse>;
	return typeof candidate.stage === 'string' && typeof candidate.message === 'string';
}

export const CLIENT_MESSAGE_ID_PREFIX = 'client-message-id-';

export function applyAIStreamEventToCache({
	event,
	queryClient,
	queryKey,
	chatRequest,
}: {
	event: ServerSentEventMessage;
	queryClient: QueryClient;
	queryKey: QueryKey;
	chatRequest: Api.AIChatRequest;
}) {
	if (event.event === 'Progress') {
		let parsed: unknown;
		try {
			parsed = JSON.parse(event.data);
		} catch {
			return;
		}
		if (!isAIChatStreamProgressResponse(parsed) || parsed.message.length === 0) {
			return;
		}
		const progress = { stage: parsed.stage, message: parsed.message };
		queryClient.setQueryData(queryKey, (messages: AIChatMessageWithProgress[] = []) => {
			return produce(messages, draftMessages => {
				const currentMessage = draftMessages[draftMessages.length - 1];
				const isStub =
					currentMessage?.role === Api.AIChatRole.Assistant && (currentMessage.contents?.length ?? 0) === 0;
				// Cache-derived order guard: once any real content is on the in-progress
				// assistant message, the stream has moved past the progress phase. Drop
				// late Progress events so the indicator can't repopulate behind real content.
				const hasRealContent =
					currentMessage?.role === Api.AIChatRole.Assistant && (currentMessage.contents?.length ?? 0) > 0;
				if (hasRealContent) return draftMessages;
				if (isStub) {
					(currentMessage as AIChatMessageWithProgress).progress = progress;
					return draftMessages;
				}
				draftMessages.push({
					id: `${CLIENT_MESSAGE_ID_PREFIX}${crypto.randomUUID()}`,
					role: Api.AIChatRole.Assistant,
					contents: [],
					progress,
				} as AIChatMessageWithProgress);
				return draftMessages;
			});
		});
		return;
	}
	if (event.event === 'Error') {
		queryClient.setQueryData(queryKey, (messages: AIChatMessageWithProgress[] = []) => {
			const errorMessage: AIChatMessageError = {
				id: `${CLIENT_MESSAGE_ID_PREFIX}${crypto.randomUUID()}`,
				error: event.data,
				chatRequest,
			};
			// Replace a trailing progress-only stub instead of appending after it, so the UI
			// doesn't briefly stack a spinner-with-text bubble above the error before invalidation.
			const last = messages[messages.length - 1] as AIChatMessageWithProgress | undefined;
			const trailingIsStub =
				last?.role === Api.AIChatRole.Assistant && (last.contents?.length ?? 0) === 0 && Boolean(last.progress);
			if (trailingIsStub) {
				return [...messages.slice(0, -1), errorMessage];
			}
			return [...messages, errorMessage];
		});
		return;
	}
	if (event.event !== 'Response' && event.event !== 'ToolResponse') {
		return;
	}
	const aiChatStreamMessageResponse = JSON.parse(event.data) as Api.AIChatStreamResponse;
	if (isAIChatStreamTextResponse(aiChatStreamMessageResponse)) {
		queryClient.setQueryData(queryKey, (messages: Api.AIChatMessage[]) => {
			const aiTextMessage: Api.AITextContent = {
				_type: 'AITextContent',
				text: aiChatStreamMessageResponse.message.text,
			};
			return produce(messages ?? [], draftMessages => {
				const currentMessage = draftMessages.length > 0 ? draftMessages[draftMessages.length - 1] : undefined;
				if (currentMessage?.role !== Api.AIChatRole.Assistant) {
					draftMessages.push({
						id: `${CLIENT_MESSAGE_ID_PREFIX}${aiChatStreamMessageResponse.id}`,
						role: Api.AIChatRole.Assistant,
						contents: [aiTextMessage],
					});
					return draftMessages;
				}
				(currentMessage as AIChatMessageWithProgress).progress = undefined;
				const lastMessageContent = currentMessage.contents[
					currentMessage.contents.length - 1
				] as Draft<Api.AITextContent>;
				if (!lastMessageContent || lastMessageContent._type !== aiTextMessage._type) {
					currentMessage.contents.push(aiTextMessage);
					return draftMessages;
				}
				if (!aiChatStreamMessageResponse.isDelta) {
					currentMessage.contents.splice(currentMessage.contents.length - 1, 1, aiTextMessage);
					return draftMessages;
				}
				lastMessageContent.text += aiTextMessage.text;
				return draftMessages;
			});
		});
	}
	if (
		isAIChatStreamToolCallResponse(aiChatStreamMessageResponse) &&
		(aiChatStreamMessageResponse.toolCall.toolName === 'lev-contact' ||
			aiChatStreamMessageResponse.toolCall.toolName === 'lev-contacts' ||
			aiChatStreamMessageResponse.toolCall.toolName === 'lev-navigation' ||
			aiChatStreamMessageResponse.toolCall.toolName === 'lev-meeting-scheduling' ||
			aiChatStreamMessageResponse.toolCall.toolName === 'lev-confirmation' ||
			aiChatStreamMessageResponse.toolCall.toolName === 'lev-website-change')
	) {
		if (aiChatStreamMessageResponse.isDelta) {
			return;
		}
		queryClient.setQueryData(queryKey, (messages: Api.AIChatMessage[]) => {
			const aiToolCallContent: Api.AIToolContent = {
				_type: 'AIToolContent',
				toolCall: aiChatStreamMessageResponse.toolCall,
			};
			return produce(messages ?? [], draftMessages => {
				const currentMessage = draftMessages.length > 0 ? draftMessages[draftMessages.length - 1] : undefined;
				if (currentMessage?.role !== Api.AIChatRole.Assistant) {
					draftMessages.push({
						id: `${CLIENT_MESSAGE_ID_PREFIX}${aiChatStreamMessageResponse.id}`,
						role: Api.AIChatRole.Assistant,
						contents: [aiToolCallContent],
					});
					return draftMessages;
				}
				(currentMessage as AIChatMessageWithProgress).progress = undefined;
				currentMessage.contents.push(aiToolCallContent);
				return draftMessages;
			});
		});
		return;
	}
	if (isAIChatStreamToolCallResponse(aiChatStreamMessageResponse)) {
		if (aiChatStreamMessageResponse.isDelta && !aiChatStreamMessageResponse.toolCall?.propertyName) {
			return;
		}
		queryClient.setQueryData(queryKey, (messages: Api.AIChatMessage[]) => {
			const aiToolCallContent: Api.AIToolContent = {
				_type: 'AIToolContent',
				toolCall: {
					toolName: aiChatStreamMessageResponse.toolCall.toolName,
					value: '',
				} as PartialToolCall,
			};
			if (aiChatStreamMessageResponse.isDelta) {
				(aiToolCallContent.toolCall as PartialToolCall).partial = {
					[aiChatStreamMessageResponse.toolCall.propertyName]: aiChatStreamMessageResponse.toolCall.value,
				};
			} else {
				aiToolCallContent.toolCall = aiChatStreamMessageResponse.toolCall;
			}
			return produce(messages ?? [], draftMessages => {
				const currentMessage = draftMessages.length > 0 ? draftMessages[draftMessages.length - 1] : undefined;
				if (currentMessage?.role !== Api.AIChatRole.Assistant) {
					draftMessages.push({
						id: `${CLIENT_MESSAGE_ID_PREFIX}${aiChatStreamMessageResponse.id}`,
						role: Api.AIChatRole.Assistant,
						contents: [aiToolCallContent],
					});
					return draftMessages;
				}
				(currentMessage as AIChatMessageWithProgress).progress = undefined;
				const lastMessageContent = currentMessage.contents[
					currentMessage.contents.length - 1
				] as Draft<Api.AIToolContent>;
				if (
					!lastMessageContent ||
					lastMessageContent._type !== aiToolCallContent._type ||
					!(lastMessageContent.toolCall as PartialToolCall)?.partial
				) {
					currentMessage.contents.push(aiToolCallContent);
					return draftMessages;
				}
				if (!aiChatStreamMessageResponse.isDelta) {
					currentMessage.contents.splice(currentMessage.contents.length - 1, 1, aiToolCallContent);
					return draftMessages;
				}
				(lastMessageContent.toolCall as PartialToolCall).partial[aiChatStreamMessageResponse.toolCall.propertyName] =
					((lastMessageContent.toolCall as PartialToolCall).partial[
						aiChatStreamMessageResponse.toolCall.propertyName
					] ?? '') + aiChatStreamMessageResponse.toolCall.value;
				return draftMessages;
			});
		});
	}
}

export function updateAIConversationCacheToolCallState({
	queryClient,
	queryKey,
	toolCallId,
	state,
}: {
	queryClient: QueryClient;
	queryKey: QueryKey;
	toolCallId: string;
	state: string | null;
}) {
	queryClient.setQueryData(queryKey, (messages: Api.AIChatMessage[] = []) =>
		produce(messages, draftMessages => {
			for (const message of draftMessages) {
				for (const content of message.contents ?? []) {
					if (content._type !== 'AIToolContent') {
						continue;
					}
					const toolCall = (content as Draft<Api.AIToolContent>).toolCall as Draft<Api.ConfirmationToolCall>;
					if (toolCall?.id !== toolCallId) {
						continue;
					}
					toolCall.state = state;
					return draftMessages;
				}
			}
			return draftMessages;
		})
	);
}
